import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { useLocation } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import { format, parseISO, subMonths } from "date-fns";
import { isEmpty, map } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient, useMutation } from "react-query";
import { CardImage } from "../../../components/CardImage";
import { DatePicker } from "../../../components/DatePicker";
import { MenuButton } from "../../../components/MenuButton";
import { getCampaigns } from "../../../services/metricsCampaigns";
import {
  putPauseCampaign,
  putContinueCampaign,
  pauseAdsCampaign,
  continueAdsCampaign,
  resendCampaign,
} from "../../../services/actionsCampaigns";
import {
  Campaigns as CampaignsType,
  Campaigns as ICampaigns,
  RequestCampaignsParams,
} from "../../../types/campaigns";
import CardDetails from "./CardDetails";
import { useDialogConfirmation } from "../../../hooks/useDialogConfirmation";
import { useToast } from "../../../hooks/useToast";
import { Button } from "@mui/material";

export function Campaigns() {
  const [cardDetails, setCardDetails] = useState<ICampaigns | null>(null);
  const [openDetails, setOpenDetails] = useState<boolean>(false);
  const [handleMore, setHandleMore] = useState<boolean>(true);
  const location = useLocation();
  const [type, setType] = useState<string | null>();
  const [startDate, setStartDate] = useState<Date>(subMonths(new Date(), 1));
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [campaignName, setCampaignName] = useState<string | null>();
  const [page, setPage] = useState<{ page: number }>({ page: 0 });

  const [campaigns, setCampaigns] = useState<Array<CampaignsType>>([]);

  const queryClient = useQueryClient();
  const dialog = useDialogConfirmation();
  const toast = useToast();

  const getCampaignsGeneral = useMutation(
    async (pageParam: number | undefined) => {
      const params: RequestCampaignsParams = {
        endDate: endDate,
        startDate: startDate,
        name: campaignName as string,
        type: type,
        page: pageParam ?? page.page,
      };
      return getCampaigns(params);
    },
    {
      onSuccess(data) {
          if(data.length < 1000 || type === 'ads' ){
            setHandleMore(false)
          }
          if(page.page === 0){
            setCampaigns(()=>{
              return [...data]
            });
          }else{
            setCampaigns((prev)=>{
              return [...prev, ...data]
            })
          }
      },
      retryDelay: 120 * 1000,
    }
  );

  const mutationPauseCampaign = useMutation(
    (id_campanha: number) => {
      dialog.loading(true);
      return putPauseCampaign(id_campanha);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("campaigns");
        toast.show("Campanha pausada com sucesso.", { severity: "success" });
        dialog.close();
      },
      onError: () => {
        toast.show("Falha ao pausar a campanha.", { severity: "error" });
      },
    }
  );

  const mutationContinueCampaign = useMutation(
    (id_campanha: number) => {
      dialog.loading(true);
      return putContinueCampaign(id_campanha);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("campaigns");
        toast.show("Campanha retomada com sucesso.", { severity: "success" });
        dialog.close();
      },
      onError: () => {
        toast.show("Falha ao continuar a campanha.", { severity: "error" });
      },
    }
  );

  const mutationPauseAdsCampaign = useMutation(
    (id_campanha: number) => {
      dialog.loading(true);
      return pauseAdsCampaign(id_campanha);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("campaigns");
        toast.show("Campanha pausada com sucesso.", { severity: "success" });
        dialog.close();
      },
      onError: () => {
        toast.show("Falha ao pausar a campanha.", { severity: "error" });
      },
    }
  );

  const mutationResendCampaign = useMutation(
    (id_campanha: number) => {
      dialog.loading(true);
      return resendCampaign(id_campanha);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("campaigns");
        toast.show("Campanha reenviada com sucesso.", { severity: "success" });
        dialog.close();
      },
      onError: () => {
        toast.show("Falha ao reenviar a campanha.", { severity: "error" });
      },
    }
  );

  const mutationContinueAdsCampaign = useMutation(
    (id_campanha: number) => {
      dialog.loading(true);
      return continueAdsCampaign(id_campanha);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("campaigns");
        toast.show("Campanha retomada com sucesso.", { severity: "success" });
        dialog.close();
      },
      onError: () => {
        toast.show("Falha ao continuar a campanha.", { severity: "error" });
      },
    }
  );

  const handleNextPage = () => {
    setPage((prev) => ({ page: prev.page + 1 }))
  }

  const handleChangeType = useCallback((changeType: string | null) => {
    setType(changeType)
  }, []);

  const handleChangeCampaignName = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setCampaignName(event.target.value);
    }

  const handleClick = useCallback(
    (campaign: ICampaigns) => {
      setCardDetails(campaign);
      setOpenDetails(!openDetails);
    },
    [openDetails]
  );

  const handleResendCampaign = useCallback(
    (id: number, type: string) => {
      setOpenDetails(false);
      const message = type.includes("WHATSAPP")
        ? "Ao reenviar a campanha, uma nova campanha com as mesmas informações será criada imediatamente e somente os contatos em espera serão impactados."
        : "Ao reenviar a campanha, uma nova campanha com as mesmas informações será criada imediatamente.";

      dialog.show("Reenviar campanha", message, {
        onConfirmate: () => {
          mutationResendCampaign.mutate(id);
        },
        onCancel: () => {
          setOpenDetails(true);
        },
      });
    },
    [dialog, mutationResendCampaign]
  );

  const handlePauseAdsCampaign = useCallback(
    (id: number) => {
      setOpenDetails(false);
      const message =
        "Ao pausar a campanha, o envio para o facebook será interrompido.";

      dialog.show("Pausar campanha", message, {
        onConfirmate: () => {
          mutationPauseAdsCampaign.mutate(id);
        },
        onCancel: () => {
          setOpenDetails(true);
        },
      });
    },
    [dialog, mutationPauseAdsCampaign]
  );

  const handlePauseCampaign = useCallback(
    (id: number) => {
      setOpenDetails(false);
      const message =
        "Ao pausar a campanha, o envio para as plataformas será interrompido.";

      dialog.show("Pausar campanha", message, {
        onConfirmate: () => {
          mutationPauseCampaign.mutate(id);
        },
        onCancel: () => {
          setOpenDetails(true);
        },
      });
    },
    [dialog, mutationPauseCampaign]
  );

  const handleContinueCampaign = useCallback(
    (id: number) => {
      setOpenDetails(false);
      const message =
        "Ao continuar a campanha, o envio para as plataformas será retomado.";

      dialog.show("Continuar campanha", message, {
        onConfirmate: () => {
          mutationContinueCampaign.mutate(id);
        },
        onCancel: () => {
          setOpenDetails(true);
        },
      });
    },
    [mutationContinueCampaign, dialog]
  );

  const handleContinueAdsCampaign = useCallback(
    (id: number) => {
      setOpenDetails(false);
      const message =
        "Ao continuar a campanha, o envio para as plataformas será retomado.";

      dialog.show("Continuar campanha", message, {
        onConfirmate: () => {
          mutationContinueAdsCampaign.mutate(id);
        },
        onCancel: () => {
          setOpenDetails(true);
        },
      });
    },
    [dialog, mutationContinueAdsCampaign]
  );


  useEffect(() => {
    if (campaigns && location.state) {
      const campaign = campaigns.find(
        (campaign) => campaign.id_campanha === location.state.campaingId
      );
      window.history.replaceState({}, document.title);
      if (campaign) {
        handleClick(campaign);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaigns]);

  const labelMenuButton = useMemo(() => {
    switch (type) {
      case "whatsapp":
        return "WhatsApp";
      case "facebook":
        return "Facebook";
      case "instagram":
        return "Instagram";
      case "sms":
        return "SMS";
      case "ads":
        return "Impulsionamento";
      default:
        return "Filtrar";
    }
  }, [type]);

  useEffect(()=>{
    getCampaignsGeneral.mutateAsync(page.page);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[page]);

  return (
    <>
      <Stack spacing={2} flex={1} pl={2}>
        <Typography variant="body1">Suas campanhas</Typography>
        <Stack spacing={1} direction="row" width="100%">
          <TextField
            sx={{ width: "80%" }}
            size="small"
            label="Procurar campanha por nome"
            onChange={handleChangeCampaignName}
          />
          <MenuButton
            color={type ? "primary" : "inherit"}
            variant="outlined"
            label={labelMenuButton}
            sx={{ ...(type === 'ads' && { fontSize: '10px' }) }}
          >
            <MenuItem
              onClick={() => {
                handleChangeType(null);
              }}
            >
              Todos
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleChangeType("facebook");
              }}
            >
              Facebook
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleChangeType("instagram");
              }}
            >
              Instagram
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleChangeType("whatsapp");
              }}
            >
              WhatsApp
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleChangeType("sms");
              }}
            >
              SMS
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleChangeType("ads");
              }}
            >
              Impulsionamento
            </MenuItem>
          </MenuButton>
        </Stack>
        <Stack spacing={1} direction="row" width="100%">
          <DatePicker
            value={startDate}
            label="De"
            fullWidth
            size="small"
            onChange={(value) => {
              setStartDate(value as Date)
            }}
          />
          <DatePicker
            disableFuture
            value={endDate}
            label="Até"
            fullWidth
            size="small"
            onChange={(value) => {
              setEndDate(value as Date);
            }}
          />
          <Button 
            sx={{
              height: '40px',
              padding: '8px',
              marginLeft: '4px',
              fontSize: getCampaignsGeneral.isLoading ? '10px' : '12px',
              fontWeight: 'bolder'
            }}
            variant="contained"
            disabled={getCampaignsGeneral.isLoading}
            onClick={()=>{ 
              setHandleMore(true);
              setPage(() => ({ page: 0 }));
            }}
          >
            {getCampaignsGeneral.isLoading ? 'Buscando' : 'Buscar'}
          </Button>
        </Stack>

        <Divider />
        <Stack 
          overflow="overlay" 
          height="62vh" 
          id={'referenceScrollable'} 
        >
          {(isEmpty(campaigns) && !getCampaignsGeneral.isLoading) ? (
            <Typography textAlign="center" variant="body1">
              Nenhuma campanha encontrada.
            </Typography>
          ) : (
            <>
            <InfiniteScroll
              scrollableTarget={'referenceScrollable'}
              hasMore={handleMore}
              dataLength={campaigns.length}
              next={() => {
                handleNextPage();
              }}
              loader={false}
              style={{
                display: "flex",
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              {map(campaigns, (campaign, index) => (
              <CardImage
                  key={`${campaign.id_campanha}`}
                  image={campaign.preview_url}
                  media_ads={campaign.media_ads}
                  title={`${index + 1}: ${campaign.nome_campanha}`}
                  type={campaign.tipo_campanha}
                  date={format(
                    parseISO(campaign.data_inicio_campanha.replace("Z", "")),
                    "dd/MM/yyyy"
                  )}
                  handleClick={() => handleClick(campaign)}
                />
              ))}
            </InfiniteScroll>
            {getCampaignsGeneral.isLoading && (
              <Box 
                display={'flex'}  
                height={'42px'}
                justifyContent={'center'}
                alignItems={'center'}
                marginTop={'8px'}
                marginBottom={'8px'}
              >
                <CircularProgress size={30}/>
              </Box>)
            }
            </>
          )}
        </Stack>
      </Stack>

      {openDetails && (
        <CardDetails
          details={cardDetails!}
          open={openDetails}
          onPause={handlePauseCampaign}
          onPauseAds={handlePauseAdsCampaign}
          onContinueAds={handleContinueAdsCampaign}
          onContinue={handleContinueCampaign}
          onResend={handleResendCampaign}
          onClose={() => setOpenDetails(false)}
        />
      )}
    </>
  );
}
